/**
 * Typed session slices for the web-call target architecture.
 *
 * These match the prop groups already used by WebCallPhaseRouter so WebCallPage
 * can build each slice once and pass them through without duplicating shapes.
 * useWebCallSession owns these slices and composes domain hooks
 * (intake / device setup / in-call) that return them.
 */
import type { AdditionalPolicy } from 'api/generated/types';
import type { AvatarImageSettings } from 'utils/avatarUtils';
import type { WebCallFunnelStep } from 'api/generated/types/webCallFunnelStep';

import type { CandidateFormData } from '../intake/CandidateForm/types';
import type { PreCallContextValue } from '../components/PreCallContext';
import type {
  CallControlsContextValue,
  CallLifecycleContextValue,
  CallMediaContextValue,
} from '../WebCallContext';
import type { CallTranscriptStore } from '../call/runtime/callTranscriptStore';

export type PrecallWarningVariant = 'weak' | 'failed' | 'blocked';

/** The `?action=` a candidate link can open the intake form on. */
export const WEB_CALL_INITIAL_ACTIONS = ['accommodation', 'opt-out'] as const;
export type WebCallInitialAction = (typeof WEB_CALL_INITIAL_ACTIONS)[number];

export interface WebCallSessionCampaign {
  jobTitle: string;
  companyName: string;
  location: string;
  brandName: string;
  aiCallerName: string;
  campaignId: string | null;
  isFetchingCampaignDetails: boolean;
}

export interface WebCallSessionBranding {
  logoUrl?: string;
  organizationName?: string;
  showPoweredByTenzo: boolean;
}

export interface WebCallSessionForm {
  languageMode?: string;
  allowedLanguages?: string[];
  encodedId?: string;
  callId: string | null;
  voiceName: string;
  isPracticeCall: boolean;
  resumeRequired?: boolean;
  existingResumeName?: string | null;
  estimatedDuration?: string;
  campaignAvatarSettings?: AvatarImageSettings | null;
  preFilledData?: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
  };
  isTestCall: boolean;
  isSharedLink?: boolean;
  initialAction?: WebCallInitialAction | null;
  additionalPolicies?: AdditionalPolicy[];
  optOutPreCallWarningOverride?: string | null;
  webCallInviteSmsEnabled?: boolean;
  /** Screen share is required but the current browser cannot share (e.g. Firefox). */
  disableDueToScreenShare?: boolean;
  onSubmit: (data: CandidateFormData) => void;
}

export interface WebCallSessionIdentification {
  personaTemplateId: string | null;
  candidateId: string | null;
  callId: string | null;
  logFunnelEvent: (step: WebCallFunnelStep) => void | Promise<void>;
  onIdentificationPassed: () => void;
}

export interface WebCallSessionPriorSession {
  processingCallId: string | null;
  onContinue: (options?: { forceRejoin?: boolean }) => void | Promise<void>;
  isContinuing: boolean;
  showForceContinue: boolean;
}

export interface WebCallSessionResuming {
  needsGesture: boolean;
  onResume: () => Promise<void> | void;
  errorMessage?: string | null;
}

export interface WebCallSessionPrecall {
  preCallValue: PreCallContextValue;
  isPrecallNetworkWarningModalOpen: boolean;
  precallWarningVariant: PrecallWarningVariant;
  onCloseNetworkWarning: () => void;
}

/** In-call shell props (modals live in session/WebCallModals). */
export interface WebCallSessionInCall {
  validated: boolean;
  expired: boolean;
  hideHeaderAndPanel: boolean;
  callEnded: boolean;
  callLifecycleValue: CallLifecycleContextValue;
  callMediaValue: CallMediaContextValue;
  callControlsValue: CallControlsContextValue;
  messagesVisible: boolean;
  showInfoPanel: boolean;
  transcriptStore: CallTranscriptStore;
  candidateEmail: string;
  optOutInCallWarningOverride?: string | null;
  onRequestAccommodation: () => void;
  onOptOutDuringCall: () => void;
  onShowOptOutConfirm: () => void;
  isTestCallOrTestEmail: boolean;
  pastCompletionRate: number | null | undefined;
  candidateId: string | null;
  callId: string | null;
  callStatus: string | null;
  encodedId: string | null;
  alternateActiveLinkEncodedId: string | null;
  deletedLinkRequestPending: boolean;
  dispositionedCandidate: boolean;
}

/** Full session payload passed to WebCallPhaseRouter (minus callPhase/isMobile). */
export interface WebCallSessionSlices {
  campaign: WebCallSessionCampaign;
  branding: WebCallSessionBranding;
  form: WebCallSessionForm;
  identification: WebCallSessionIdentification;
  priorSession: WebCallSessionPriorSession;
  resuming: WebCallSessionResuming;
  precall: WebCallSessionPrecall;
  inCall: WebCallSessionInCall;
}

/** Aliases used by WebCallPhaseRouter (same shapes as the Session* interfaces). */
export type WebCallCampaignSlice = WebCallSessionCampaign;
export type WebCallBrandingSlice = WebCallSessionBranding;
export type WebCallFormSlice = WebCallSessionForm;
export type WebCallIdentificationSlice = WebCallSessionIdentification;
export type WebCallPriorSessionSlice = WebCallSessionPriorSession;
export type WebCallResumingSlice = WebCallSessionResuming;
export type WebCallPrecallSlice = WebCallSessionPrecall;
export type WebCallInCallSlice = WebCallSessionInCall;
